import { AppstoreOutlined, SearchOutlined } from "@ant-design/icons";
import { PERMISSIONS, hasPermission } from "@nimbus-erp/shared";
import { Empty, Input, Modal } from "antd";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { ErpGlyph } from "./ErpGlyph";

const routes = [
  {
    path: "/analytics",
    code: "BI",
    title: "经营分析与预警",
    description: "实际收入与成本、缺料交付、预警分派与闭环",
    icon: <ErpGlyph name="ledger" />,
    permission: PERMISSIONS.analyticsRead,
  },
  {
    path: "/manufacturing",
    code: "ENG",
    title: "制造工程",
    description: "BOM 版本、工艺路线、工作中心与日历",
    icon: <ErpGlyph name="manufacturing" />,
    permission: PERMISSIONS.manufacturingRead,
  },
  {
    path: "/production",
    code: "MO",
    title: "生产执行",
    description: "工单、领退料、报工、返工与完工入库",
    icon: <ErpGlyph name="manufacturing" />,
    permission: PERMISSIONS.manufacturingRead,
  },
  {
    path: "/planning",
    code: "MRP",
    title: "计划与排产",
    description: "预测、MPS、MRP、缺料、能力与排产",
    icon: <ErpGlyph name="ledger" />,
    permission: PERMISSIONS.planningRead,
  },
  {
    path: "/quality",
    code: "QC",
    title: "质量管理",
    description: "检验标准、来料隔离、过程完工检验与不合格",
    icon: <ErpGlyph name="security" />,
    permission: PERMISSIONS.qualityRead,
  },
  {
    path: "/workflow",
    title: "审批与待办",
    code: "APV",
    description: "采购、付款、超领和让步审批与模板配置",
    icon: <ErpGlyph name="security" />,
    permission: PERMISSIONS.workflowRead,
  },
  {
    path: "/equipment",
    code: "EQ",
    title: "设备管理",
    description: "点检、保养、故障维修、备件与停复机",
    icon: <ErpGlyph name="manufacturing" />,
    permission: PERMISSIONS.equipmentRead,
  },
  {
    path: "/workspace",
    code: "HOME",
    permission: PERMISSIONS.dashboardView,
    title: "工作台",
    description: "今日工作、基础数据与快捷操作",
    icon: <ErpGlyph name="ledger" />,
  },
  {
    path: "/sales",
    code: "SAL",
    title: "销售管理",
    description: "销售订单、审核、出库与退货",
    icon: <ErpGlyph name="sales" />,
    permission: PERMISSIONS.salesRead,
  },
  {
    path: "/purchase",
    code: "PUR",
    title: "采购管理",
    description: "采购订单、审核、入库与退货",
    icon: <ErpGlyph name="purchase" />,
    permission: PERMISSIONS.purchaseRead,
  },
  {
    path: "/inventory",
    code: "INV",
    title: "库存管理",
    description: "库存余额、流水、调拨与盘点",
    icon: <ErpGlyph name="inventory" />,
    permission: PERMISSIONS.inventoryRead,
  },
  {
    path: "/settlement",
    code: "ARAP",
    title: "往来资金",
    description: "应收应付、收付款、核销与账龄",
    icon: <ErpGlyph name="settlement" />,
    permission: PERMISSIONS.settlementRead,
  },
  {
    path: "/finance",
    code: "FIN",
    title: "财务核算",
    description: "科目、凭证、余额表与总账",
    icon: <ErpGlyph name="finance" />,
    permission: PERMISSIONS.financeRead,
  },
  {
    path: "/master-data",
    code: "MDM",
    title: "基础资料",
    description: "商品、客户、供应商和仓库",
    icon: <ErpGlyph name="master" />,
    permission: PERMISSIONS.masterDataRead,
  },
  {
    path: "/organization",
    code: "ORG",
    title: "组织与员工",
    description: "部门、员工和角色查询",
    permission: PERMISSIONS.organizationRead,
    icon: <ErpGlyph name="organization" />,
  },
  {
    path: "/subscription",
    code: "SUB",
    title: "套餐与用量",
    description: "套餐状态、到期时间与资源额度",
    icon: <ErpGlyph name="subscription" />,
  },
  {
    path: "/access",
    code: "ACL",
    title: "权限与安全",
    description: "查看当前账号的授权边界",
    icon: <ErpGlyph name="security" />,
  },
];

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const visibleRoutes = useMemo(() => {
    const normalized = keyword.trim().toLowerCase();
    return routes.filter(
      (route) =>
        (!("permission" in route) ||
          (!!user && hasPermission(user.permissions, route.permission))) &&
        (!normalized ||
          `${route.title}${route.description}`
            .toLowerCase()
            .includes(normalized) ||
          route.code.toLowerCase().includes(normalized)),
    );
  }, [keyword, user]);

  const close = () => {
    setKeyword("");
    onClose();
  };

  const go = (path: string) => {
    navigate(path);
    close();
  };

  return (
    <Modal
      className="command-modal"
      open={open}
      footer={null}
      onCancel={close}
      width={760}
      centered
      destroyOnHidden
      transitionName=""
      maskTransitionName=""
    >
      <header className="erp-locator__head">
        <div className="erp-locator__identity">
          <span className="erp-locator__brand-mark">
            <AppstoreOutlined />
          </span>
          <div>
            <small>YUNMAO / BUSINESS LOCATOR</small>
            <strong>业务定位</strong>
          </div>
        </div>
        <span>快速进入模块、单据与业务工作台</span>
      </header>
      <div className="erp-locator__body">
        <div className="erp-locator__query">
          <SearchOutlined />
          <Input
            autoFocus
            allowClear
            variant="borderless"
            placeholder="例如：销售订单、库存、客户"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onPressEnter={() => visibleRoutes[0] && go(visibleRoutes[0].path)}
          />
          <kbd>ENTER</kbd>
        </div>
        <div className="erp-locator__caption">
          <span>可用业务模块</span>
          <span>{visibleRoutes.length.toString().padStart(2, "0")} 项</span>
        </div>
        <div className="command-list">
          {visibleRoutes.length ? (
            visibleRoutes.map((route) => (
              <button
                key={route.path}
                type="button"
                onClick={() => go(route.path)}
              >
                <span className="command-list__icon">{route.icon}</span>
                <span>
                  <strong>{route.title}</strong>
                  <small>{route.description}</small>
                </span>
                <code>{route.code}</code>
              </button>
            ))
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="没有匹配的功能"
            />
          )}
        </div>
        <footer className="erp-locator__footer">
          <span>输入关键词筛选业务模块</span>
          <span>
            <kbd>Enter</kbd> 打开首项 <kbd>Esc</kbd> 关闭
          </span>
        </footer>
      </div>
    </Modal>
  );
}
